// T-462 — "Controllo prezzi": rilevatore deterministico di COSTI ANOMALI dei
// prodotti (foods.last_unit_cost). PURO — nessun I/O. Nasce dal caso Rösti Rounds
// (€3,32 "al pezzo" ma in realtà €/kg, caricato in unità miste kg+pz) sfuggito
// alla soglia fissa €25 del gate d'import T-460: qui il segnale non è il valore
// assoluto ma l'INCOERENZA (unità miste, unità del costo ≠ unità del prodotto,
// salto vs storico, outlier di categoria). Consumato dal Food Cost per la lista
// "questi costi sembrano sbagliati", con tap → correzione del costo per-alimento.

import { convertQty } from './uomConvert.js';
const costLabel = (value: number) => value.toLocaleString('it-IT', { maximumFractionDigits: 4 });

export type CostAnomalyKind =
  | 'unita_miste' // carichi in ≥2 unità canoniche diverse (kg e pz) → costo/giacenza inaffidabili
  | 'costo_unita_diversa' // costo caricato in un'unità ≠ default_uom del prodotto
  | 'costo_pezzo_alto' // prodotto contato a pezzi con costo/pezzo implausibile
  | 'salto_costo' // last_unit_cost molto maggiore del costo precedente
  | 'outlier_categoria'; // costo molto sopra la mediana di categoria (stessa uom)

export interface FoodCostSignal {
  id: string;
  name: string;
  category: string | null;
  defaultUom: string | null;
  costUom?: string | null;
  lastUnitCost: number | null;
  prevUnitCost: number | null;
  /** Unità canoniche DISTINTE viste nei carichi (stock_in) del prodotto. */
  loadUoms?: string[];
  /** Osservazioni con data: consentono di separare errori storici già revisionati dai nuovi carichi. */
  loadUomObservations?: Array<{ uom: string; observedAt: string | null }>;
  /** Conferma umana dell'UdM anagrafica; non modifica gli eventi storici. */
  uomReviewedAt?: string | null;
  uomReviewedAs?: string | null;
}

export interface CostAnomaly {
  foodId: string;
  name: string;
  kind: CostAnomalyKind;
  severity: 'alta' | 'media' | 'bassa';
  currentCost: number | null;
  currentUom: string | null;
  observedUoms?: string[];
  latestLoadUom?: string | null;
  latestLoadAt?: string | null;
  /** Spiegazione leggibile (mostrata all'utente). */
  detail: string;
}

// Unità "a singolo pezzo" (un articolo = un pezzo), incl. le sigle che
// canonicalizzano a "pz" (allineato a validateLine.ts SINGLE_PIECE_UOMS).
const SINGLE_PIECE_UOMS = new Set<string>([
  'pz', 'pezzo', 'pezzi', 'pzi', 'nr', 'n', 'num', 'cad', 'cadauno', 'ud', 'ea',
]);

// Soglie (documentate, calibrabili). Sono per una LISTA di revisione, non un
// blocco automatico → falsi positivi ok (l'operatore conferma/ignora).
const PIECE_COST_BAR = 5; // €/pz: un singolo pezzo di ALIMENTO oltre = sospetto (collo non ripartito)
const SALTO_MULT = 5; // last > 5× prev = salto sospetto (spesso cambio unità all'import)
const OUTLIER_MULT = 4; // costo > 4× mediana categoria+uom = outlier
const OUTLIER_MIN_GROUP = 4; // servono ≥4 prodotti confrontabili per una mediana affidabile

function norm(u: string | null | undefined): string {
  return (u ?? '').trim().toLowerCase().replace(/\.+$/, '');
}

function median(nums: number[]): number {
  const s = [...nums].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 === 0 ? (s[mid - 1] + s[mid]) / 2 : s[mid];
}

function observedLoads(f: FoodCostSignal): Array<{ uom: string; observedAt: string | null }> {
  const source = f.loadUomObservations
    ?? (f.loadUoms ?? []).map((uom) => ({ uom, observedAt: null }));
  const reviewedAt = f.uomReviewedAt ? Date.parse(f.uomReviewedAt) : Number.NaN;
  const reviewMatchesCurrentUom = Number.isFinite(reviewedAt)
    && norm(f.uomReviewedAs) === norm(f.defaultUom);
  if (!reviewMatchesCurrentUom) return source;
  return source.filter((item) => {
    const observedAt = item.observedAt ? Date.parse(item.observedAt) : Number.NaN;
    // Un'osservazione senza data non può essere dichiarata precedente alla
    // revisione: fail closed, resta nel controllo.
    return !Number.isFinite(observedAt) || observedAt > reviewedAt;
  });
}

/**
 * Rileva i costi anomali. Un prodotto compare AL PIÙ una volta, con l'anomalia
 * più importante (priorità: unità miste → salto → unità diversa → costo/pezzo →
 * outlier). Ordinato per severità (alta → bassa), poi per costo decrescente.
 */
export function detectCostAnomalies(foods: FoodCostSignal[]): CostAnomaly[] {
  // Mediane per (categoria, uom canonica) per l'outlier — solo gruppi ≥ soglia.
  const groups = new Map<string, number[]>();
  for (const f of foods) {
    if (f.lastUnitCost == null || !(f.lastUnitCost > 0)) continue;
    if (!f.category || f.category === 'altro') continue;
    const key = `${f.category}|${norm(f.costUom ?? f.defaultUom)}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(f.lastUnitCost);
  }
  const medians = new Map<string, number>();
  for (const [key, vals] of groups) {
    if (vals.length >= OUTLIER_MIN_GROUP) medians.set(key, median(vals));
  }

  const out: CostAnomaly[] = [];
  for (const f of foods) {
    const uom = norm(f.defaultUom);
    const cost = f.lastUnitCost;
    // Controllo PREZZI: senza un costo non c'è un valore da giudicare "sbagliato".
    // Il costo MANCANTE su prodotti usati è coperto altrove (indice affidabilità
    // T-343), non è un'anomalia di valore.
    if (cost == null || !(cost > 0)) continue;
    const observations = observedLoads(f);
    const loads = observations.map((item) => convertQty(1, item.uom, f.defaultUom ?? '') != null ? norm(f.defaultUom) : norm(item.uom)).filter(Boolean);
    const distinctLoads = [...new Set(loads)];
    const dated = observations
      .map((item) => ({ ...item, time: item.observedAt ? Date.parse(item.observedAt) : Number.NaN }))
      .filter((item) => Number.isFinite(item.time))
      .sort((a, b) => b.time - a.time);
    const latest = dated[0] ?? observations[observations.length - 1] ?? null;
    const base = {
      foodId: f.id, name: f.name, currentCost: cost, currentUom: f.costUom ?? f.defaultUom ?? null,
      observedUoms: distinctLoads, latestLoadUom: latest?.uom ?? null, latestLoadAt: latest?.observedAt ?? null,
    };

    // 1) unità miste: carichi in ≥2 unità canoniche diverse → tutto inaffidabile.
    if (distinctLoads.length >= 2) {
      out.push({ ...base, kind: 'unita_miste', severity: 'alta',
        detail: `Nello storico compare in unità diverse (${distinctLoads.join(', ')}). Anagrafica: ${f.defaultUom ?? '—'}${latest ? `; ultimo carico: ${latest.uom}` : ''}. Verifica e conferma l'unità corretta.` });
      continue;
    }

    // 2) salto di costo: last >> prev (spesso l'import ha cambiato unità).
    if (cost != null && f.prevUnitCost != null && f.prevUnitCost > 0 && cost > f.prevUnitCost * SALTO_MULT) {
      out.push({ ...base, kind: 'salto_costo', severity: 'alta',
        detail: `Il costo è passato da €${costLabel(f.prevUnitCost)} a €${costLabel(cost)} (${Math.round(cost / f.prevUnitCost)}×): verifica unità e documento.` });
      continue;
    }

    // 3) unità del costo diversa dall'unità del prodotto (un solo load-uom, ≠ default).
    if ((f.costUom && uom && convertQty(1, f.costUom, uom) == null)
      || (!f.costUom && distinctLoads.length === 1 && uom && distinctLoads[0] !== uom)) {
      out.push({ ...base, kind: 'costo_unita_diversa', severity: 'media',
        detail: `Prodotto in "${f.defaultUom}", costo in "${f.costUom ?? distinctLoads[0]}": conversione non disponibile. Verifica il formato nel documento.` });
      continue;
    }

    // 4) costo/pezzo alto per un contato: un singolo pezzo di alimento oltre soglia.
    if (cost != null && SINGLE_PIECE_UOMS.has(norm(f.costUom ?? uom)) && cost > PIECE_COST_BAR) {
      out.push({ ...base, kind: 'costo_pezzo_alto', severity: 'media',
        detail: `Conferma cosa rappresenta il pezzo a €${costLabel(cost)}: unità singola, bottiglia o confezione. Il prezzo da solo non dimostra un errore.` });
      continue;
    }

    // 5) outlier di categoria: costo molto sopra la mediana dei simili (stessa uom).
    if (cost != null && cost > 0) {
      const key = `${f.category ?? '?'}|${norm(f.costUom ?? uom)}`;
      const med = medians.get(key);
      if (med != null && med > 0 && cost > med * OUTLIER_MULT) {
        out.push({ ...base, kind: 'outlier_categoria', severity: 'bassa',
          detail: `€${costLabel(cost)}/${f.costUom ?? f.defaultUom} è circa ${Math.round(cost / med)} volte la mediana della categoria (€${costLabel(med)}). Verifica formato e qualità prima di confrontare.` });
        continue;
      }
    }
  }

  const rank = { alta: 0, media: 1, bassa: 2 } as const;
  return out.sort((a, b) => rank[a.severity] - rank[b.severity] || (b.currentCost ?? 0) - (a.currentCost ?? 0));
}
